/** @jsx jsx */
/* eslint-disable jsx-a11y/accessible-emoji, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, max-len, no-nested-ternary, react/no-array-index-key */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { css, jsx } from '@emotion/core';
import {
  BoardData, DayHeader, RequiredStaffInfo, ShiftItem, SimulationShift, StaffInfo, ViewType,
} from 'domain/attend/shiftBoard/types';
import {
  defaultTimeFormatter, DOW_LABELS, effectiveWorkMinutes, isWorkCalculated, parseDateStr, TimeFormatter, toDateStr,
} from 'domain/attend/shiftBoard/time';
import { CellState } from 'components/pages/master/attend/ShiftBoardPage/useShiftBoard';
import { photoPathByStaffCode } from 'hooks/usePhotoPath';
import { PatternColor } from './PatternPalette';
import StaffAvatar from './StaffAvatar';
import { LABOR_COST_TIP, mock, TRANSPORTATION_TIP } from './styles';

// 列幅（9/24 案D）。iPad 横（1024px〜）でサイドメニューを閉じれば半月 16 日が横スクロールなしに収まる。パソコンも同じ
// 名前 128 + 1 日 46 以上 × 16 + 合計 96 = 960px 以上。
// 1 日の最小幅は、iPad 横（1024px）でサイドメニューを閉じたときに半月 16 日が収まる幅。そのときの表の幅は約 991px で、
// 48px だと 992px になり 1px はみ出していたので 46px にした（約 30px の余裕）。40px まで詰めるとカードの「シフトA」「08:45」が切れる。
// サイドメニューを開いて幅が足りないときは詰めずに横スクロールにする（名前の列は左に残る）。9/30
const NAME_W = 128;
/**
 * 右端の合計の列（9/24 に合計・人件費・交通費の 3 列を 1 列にまとめた）
 * 9/25 に項目名（人件費・交通費）を付けるため 84 → 96px にした
 */
const SUM_W = 96;
const COL_MIN = 46;
// 1 ヶ月のときも 1 日の幅は半月と同じにする（9/30）。31 日を画面に詰めると 1 日 40px 前後になり、カードの文字が「シ…」「08:…」と切れていた。
// 半月（最大 16 日）がこの画面で取る 1 日の幅を最小幅にし、はみ出すぶんは横にスクロールする
const HALF_DAYS = 16;
// 列の最小幅は CSS 変数で渡す（表示単位と画面幅で変わるため）
const COL_MIN_VAR = `var(--sb-col-min, ${COL_MIN}px)`;

const styles = {
  card: css({
    // ヘッダー・本体・フッターで列幅を揃えるため、表の中は border-box に統一する
    '& *': { boxSizing: 'border-box' },
    flex: 1,
    minWidth: 0,
    background: '#fff',
    // 9/25 に内側の余白（12px）をやめて罫線で囲み、そのぶん表を広げた。上端は見出しの行の罫線を使う
    borderLeft: `1px solid ${mock.primaryM80}`,
    borderRight: `1px solid ${mock.primaryM80}`,
    borderBottom: `1px solid ${mock.primaryM80}`,
  }),
  scroll: css({ overflowX: 'auto' }),
  row: css({ display: 'flex', borderBottom: `1px solid ${mock.gray10}` }),
  headRow: css({
    display: 'flex', position: 'sticky', top: 0, background: '#fff', zIndex: 5,
  }),
  // 月をまたぐ期間のときだけ出す月の行（9/16 決定）
  monthRow: css({
    display: 'flex', background: '#fff', zIndex: 5,
  }),
  monthCell: css({
    borderTop: `1px solid ${mock.primaryM80}`,
    borderLeft: `1px solid ${mock.gray10}`,
    background: mock.primaryM95,
    textAlign: 'center',
    fontSize: '11px',
    fontWeight: 700,
    color: mock.textMain,
    padding: '3px 0',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  }),
  headCell: css({
    background: mock.primaryM95, borderTop: `1px solid ${mock.primaryM80}`, borderBottom: `1px solid ${mock.primaryM80}`, fontSize: '11px', fontWeight: 700,
  }),
  dateHead: css({
    flex: '1 1 0', minWidth: COL_MIN_VAR, overflow: 'hidden', borderTop: `1px solid ${mock.primaryM80}`, borderBottom: `1px solid ${mock.primaryM80}`, borderLeft: `1px solid ${mock.gray10}`, textAlign: 'center', padding: '4px 0',
  }),
  nameCell: css({
    width: `${NAME_W}px`, flex: 'none', padding: '6px 8px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', position: 'sticky', left: 0, zIndex: 4,
  }),
  avatar: css({
    width: '22px', height: '22px', borderRadius: '9999px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, flex: 'none',
  }),
  name: css({
    fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  }),
  cell: css({
    flex: '1 1 0',
minWidth: COL_MIN_VAR,
height: '60px',
borderLeft: `1px solid ${mock.gray10}`,
position: 'relative',
padding: '3px',
boxSizing: 'border-box',
    '&:hover': { boxShadow: `inset 0 0 0 2px ${mock.primaryM80}` },
    '&:hover [data-addhint]': { opacity: 1 },
    '@keyframes sbFlash': { '0%': { boxShadow: `inset 0 0 0 3px ${mock.secondary}` }, '100%': { boxShadow: 'inset 0 0 0 0 transparent' } },
    '&[data-flash="true"]': { animation: 'sbFlash .7s ease' },
    '&[data-drop="true"]': { boxShadow: `inset 0 0 0 2px ${mock.primary}` },
  }),
  wish: css({
    position: 'absolute', left: '3px', top: '2px', fontSize: '9px', fontWeight: 500,
  }),
  chips: css({
    position: 'absolute', left: '3px', right: '3px', bottom: '3px', display: 'flex', flexDirection: 'column', gap: '2px',
  }),
  chip: css({
    borderRadius: '3px', fontWeight: 700, textAlign: 'center', padding: '1px 0', lineHeight: 1.4, touchAction: 'none', userSelect: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  }),
  addHint: css({
    position: 'absolute', inset: '3px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', opacity: 0, transition: 'opacity .12s', pointerEvents: 'none',
  }),
  addHintInner: css({
    width: '100%', border: `1px dashed ${mock.gray20}`, borderRadius: '3px', color: mock.gray40, fontSize: '11px', textAlign: 'center', padding: '2px 0',
  }),
  memo: css({
    position: 'absolute', left: '2px', bottom: '2px', width: 0, height: 0, borderLeft: `6px solid ${mock.primaryP20}`, borderTop: '6px solid transparent',
  }),
  // 保存に失敗したセルの印（9/30）。理由はツールチップで出す
  failMark: css({
    position: 'absolute', right: '2px', top: '2px', width: '14px', height: '14px', borderRadius: '9999px', background: mock.error, color: '#fff', fontSize: '10px', fontWeight: 700, lineHeight: '14px', textAlign: 'center', zIndex: 2, pointerEvents: 'auto',
  }),
  changedDot: css({
    position: 'absolute', right: '2px', top: '2px', width: '6px', height: '6px', borderRadius: '9999px', background: mock.secondary,
  }),
  lockMark: css({
    position: 'absolute', right: '3px', bottom: '2px', fontSize: '9px', color: mock.gray40,
  }),
  totalCell: css({
    flex: 'none', borderLeft: `1px solid ${mock.gray10}`, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 8px',
  }),
  footRow: css({ display: 'flex', borderTop: `1px solid ${mock.primaryM80}` }),
  footLabel: css({
    width: `${NAME_W}px`, flex: 'none', padding: '6px 8px', fontSize: '11px', fontWeight: 700, position: 'sticky', left: 0, zIndex: 4, background: 'inherit',
  }),
  footCell: css({
    flex: '1 1 0', minWidth: COL_MIN_VAR, borderLeft: `1px solid ${mock.gray10}`, padding: '6px 3px', fontSize: '10px', fontWeight: 700, overflow: 'hidden', whiteSpace: 'nowrap', position: 'relative',
  }),
  footTotal: css({
    flex: 'none', borderLeft: `1px solid ${mock.gray10}`, padding: '6px 6px', fontSize: '11px', fontWeight: 700, overflow: 'hidden', whiteSpace: 'nowrap',
  }),
  mark: css({
    position: 'absolute', left: '6px', top: '50%', transform: 'translateY(-50%)', fontSize: '9px', lineHeight: 1, pointerEvents: 'none',
  }),
};

/**
 * 本人の希望（参考表示）。label は「9:00-18:00」のような時刻、時刻がなければパターン略称
 *
 * 希望との一致・不一致は判定しない（9/24 決定。希望は参考で、「希望どおりにする」を使えばそのとおりになる）。
 */
export type HopeLabel = {
  label: string;
  isOff: boolean;
  patternId?: string | null;
  start?: string | null;
  end?: string | null;
} | null;

export type BoardGridProps = {
  board: BoardData;
  view: ViewType;
  isSimulating: boolean;
  editable: boolean;
  bulkMode: boolean;
  bulkHint: string;
  getCell: (staffCode: string, date: string) => CellState;
  awayShifts: (staffCode: string, date: string) => Array<ShiftItem>;
  hopeOf: (staffCode: string, date: string) => HopeLabel;
  colorOf: (patternId: string | null) => PatternColor;
  staffSubmitted: (staffCode: string) => boolean | null;
  onCellClick: (staff: StaffInfo, day: DayHeader, index: number | null) => void;
  /** 保存に失敗したセルの理由（無ければ null）。セルに「!」を出す（9/30） */
  failedOf?: (staffCode: string, date: string) => string | null;
  onCellClear: (staff: StaffInfo, day: DayHeader) => void;
  onCellLongPress: (staff: StaffInfo, day: DayHeader) => void;
  onStaffTap: (staff: StaffInfo) => void;
  onChipDragStart: (staff: StaffInfo, day: DayHeader, index: number, e: React.PointerEvent) => void;
  flashKey: string | null;
  dropKey: string | null;
  showLaborCost: boolean;
  /** 時間量の整形。60 進/10 進とその丸めは労務規約項目マスタに合わせる（9/23） */
  formatTime?: TimeFormatter;
};

const md = (s: string): string => {
  const d = parseDateStr(s);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

/**
 * 月をまたぐ期間で「9月」「10月」の行を作るための区切り（9/16 決定）
 *
 * 同じ月が続く列をまとめ、月が変わるところで新しい区切りにする。
 * 月をまたがないときは空を返し、行そのものを出さない。
 */
const monthSpans = (dates: Array<string>): Array<{ label: string; span: number }> => {
  const out: Array<{ label: string; span: number }> = [];
  dates.forEach((date) => {
    const d = parseDateStr(date);
    const label = `${d.getFullYear()}年${d.getMonth() + 1}月`;
    const last = out[out.length - 1];
    if (last && last.label === label) {
      last.span += 1;
    } else {
      out.push({ label, span: 1 });
    }
  });
  return out.length > 1 ? out : [];
};
const yen = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : `¥${Math.round(n).toLocaleString('ja-JP')}`);
const dowColor = (dow: number): string => (dow === 0 ? mock.secondary : dow === 6 ? mock.primary : mock.textSub);

/** カードの文言。distinguish は、そのスタッフが法定／所定を区別するか（区別しない人の公休はいつも「公休」。10/5） */
const chipLabel = (s: SimulationShift, distinguish: boolean): string => {
  if (s.attendType !== 0) {
    return distinguish && s.attendType === 1 ? '公休(法)' : '公休';
  }
  if (!s.shiftPatternId && s.start && s.end) {
    return `${s.start}–${s.end}`;
  }
  return s.shiftPatternShortName || 'ﾌﾘｰ';
};

// 勤務時間は計算済みならサーバの値、未計算（確定前）なら開始・終了・休憩からの目安（9/25）
const workOf = (s: SimulationShift): number => effectiveWorkMinutes(s);
const uncalculated = (s: SimulationShift): boolean => !s.deleted && s.attendType === 0 && !isWorkCalculated(s);
const NOT_YET = '未計算';

type FootCell = { text: string; fg: string; bg: string; mark: string; markFg: string; tip: string };
/** labelTip: 行名にマウスを当てたときの説明（10/5） */
type FootRow = { label: string; labelTip?: string; bg: string; align: 'center' | 'right'; cells: Array<FootCell>; total: string };
const plain = (text: string, fg: string = mock.textMain, tip: string = ''): FootCell => ({
  text, fg, bg: 'transparent', mark: '', markFg: 'transparent', tip,
});

/**
 * モック準拠のシフト表（週・半月・月）。行 = スタッフ、列 = 日。
 * クリックで編集／一括登録、右クリックで割当解除、長押しでセル編集、チップをドラッグで移動（マウスだけ）。
 */
const BoardGrid: React.FC<BoardGridProps> = ({
  board, view, isSimulating, editable, bulkMode, bulkHint, getCell, awayShifts, hopeOf, colorOf, staffSubmitted,
  onCellClick, onCellClear, onCellLongPress, failedOf, onStaffTap, onChipDragStart, flashKey, dropKey, showLaborCost,
  formatTime = defaultTimeFormatter,
}) => {
  const hours = formatTime;
  // 売上（予算・実績）の表示可否（9/16 決定）。人件費予算・交通費は人件費と同じ権限にまとめた（9/17 決定）
  const showSales = board.role.canViewSales !== false;
  const pressTimer = useRef<number | null>(null);
  const pressOrigin = useRef<{ x: number; y: number } | null>(null);
  const longPressed = useRef(false);
  // 顔写真の URL はキャッシュを効かせるため、スタッフ一覧が変わったときだけ作り直す
  const photoUrls = useMemo(() => {
    const m: { [code: string]: string } = {};
    board.staffs.forEach((s) => {
      m[s.staffCode] = photoPathByStaffCode(s.staffCode, s.nameplate || undefined);
    });
    return m;
  }, [board.staffs]);
  const photoOf = (s: StaffInfo) => photoUrls[s.staffCode] || '';
  const today = toDateStr(new Date());
  // 表の幅を測り、半月より長い期間（1 ヶ月・長い期間指定）は半月と同じ 1 日の幅を最小幅にする
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollW, setScrollW] = useState(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return undefined;
    }
    const measure = () => setScrollW(el.clientWidth);
    measure();
    const RO = (window as any).ResizeObserver;
    if (RO) {
      const ro = new RO(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const colMin = board.days.length > HALF_DAYS && scrollW > 0 ? Math.max(COL_MIN, Math.floor((scrollW - NAME_W - SUM_W) / HALF_DAYS)) : COL_MIN;


  const required: { [d: string]: RequiredStaffInfo } = {};
  board.requiredStaff.forEach((r) => {
    required[r.date] = r;
  });
  const byDate: { [d: string]: BoardData['summary']['byDate'][0] } = {};
  board.summary.byDate.forEach((d) => {
    byDate[d.date] = d;
  });
  const byStaff: { [c: string]: BoardData['summary']['byStaff'][0] } = {};
  board.summary.byStaff.forEach((s) => {
    byStaff[s.staffCode] = s;
  });
  const vioCells: { [k: string]: Array<string> } = {};
  board.violations.forEach((v) => v.dates.forEach((d) => {
    const k = `${v.staffCode}|${d}`;
    vioCells[k] = (vioCells[k] || []).concat([v.message]);
  }));
  const vioStaff: { [c: string]: boolean } = {};
  board.violations.forEach((v) => {
    vioStaff[v.staffCode] = true;
  });

  // 人数・時間はいつもセルから数える（9/25）。未計算のシフトは目安で、人件費・交通費は「未計算」と出す
  const perDay = board.days.map((day) => {
    let count = 0;
    let minutes = 0;
    let uncalc = false;
    board.staffs.forEach((st) => {
      const live = getCell(st.staffCode, day.date).shifts.filter((s) => !s.deleted && s.attendType === 0);
      if (live.length > 0) {
        count += 1;
        minutes += live.reduce((t, s) => t + workOf(s), 0);
      }
      if (live.some(uncalculated)) {
        uncalc = true;
      }
    });
    const d = byDate[day.date];
    const r = required[day.date];
    return {
      date: day.date,
      count,
      minutes,
      uncalc,
      plan: r ? r.required : null,
      costBudget: d ? d.laborCostBudget : null,
      cost: d ? d.laborCost : null,
      trans: d ? d.transportation : null,
      sales: d ? d.salesBudget : null,
      salesAct: d ? d.salesActual : null,
      rate: d ? d.laborCostRate : null,
    };
  });
  const sum = (f: (x: typeof perDay[0]) => number | null): number | null => {
    let any = false;
    const t = perDay.reduce((acc, x) => {
      const v = f(x);
      if (v === null) {
        return acc;
      }
      any = true;
      return acc + v;
    }, 0);
    return any ? t : null;
  };
  const anyUncalc = perDay.some((x) => x.uncalc);
  const totalCost = board.summary.total.laborCost;
  const totalSales = board.summary.total.salesBudget;
  const totalRate = board.summary.total.laborCostRate;

  const clearPress = () => {
    if (pressTimer.current !== null) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };
  const pointerDown = (staff: StaffInfo, day: DayHeader) => (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse') {
      return;
    }
    longPressed.current = false;
    pressOrigin.current = { x: e.clientX, y: e.clientY };
    clearPress();
    pressTimer.current = window.setTimeout(() => {
      longPressed.current = true;
      onCellLongPress(staff, day);
    }, 500);
  };
  const pointerMove = (e: React.PointerEvent) => {
    if (pressOrigin.current && (Math.abs(e.clientX - pressOrigin.current.x) > 8 || Math.abs(e.clientY - pressOrigin.current.y) > 8)) {
      clearPress();
      pressOrigin.current = null;
    }
  };
  const pointerUp = () => {
    clearPress();
    pressOrigin.current = null;
  };

  const footRows: Array<FootRow> = [
    {
      label: '日別の人数',
bg: mock.primaryM95,
align: 'center',
      cells: perDay.map((x) => {
        const over = x.plan !== null && x.count > x.plan;
        const under = x.plan !== null && x.count < x.plan;
        return {
          text: String(x.count),
          fg: over ? mock.amberText : under ? mock.secondary : mock.textMain,
          bg: over ? mock.amberBg : under ? mock.errorBg : 'transparent',
          mark: over ? '▲' : under ? '▼' : '',
          markFg: over ? mock.amberMark : mock.secondary,
          tip: x.plan === null ? `${md(x.date)}：割当${x.count}名` : `${md(x.date)}：割当${x.count}名 / 予定${x.plan}名${over ? `（${x.count - x.plan}名超過）` : under ? `（${x.plan - x.count}名不足）` : ''}`,
        };
      }),
      total: String(perDay.reduce((t, x) => t + x.count, 0)),
    },
    {
      label: '予定人数',
bg: mock.primaryM95,
align: 'center',
      cells: perDay.map((x) => plain(x.plan === null ? '—' : String(x.plan), mock.textSub)),
      total: String(perDay.reduce((t, x) => t + (x.plan || 0), 0)),
    },
    {
      label: '総労働時間',
bg: mock.primaryM95,
align: 'right',
      cells: perDay.map((x) => plain(`${hours(x.minutes)}`, mock.textMain, x.uncalc ? '確定して公開すると正しく計算されます（今は開始・終了・休憩からの目安）' : '')),
      total: `${hours(perDay.reduce((t, x) => t + x.minutes, 0))}`,
    },
  ];
  // 人件費・人件費予算・交通費は、正社員（項目2）とアルバイト（項目3）の権限で出す（9/17 決定）
  if (showLaborCost) {
    footRows.push(
      {
        label: '人件費予算',
bg: '#fff',
align: 'right',
        cells: perDay.map((x) => plain(yen(x.costBudget), mock.textSub)),
        total: yen(sum((x) => x.costBudget)),
      },
      {
        label: '人件費',
        labelTip: LABOR_COST_TIP,
bg: '#fff',
align: 'right',
        cells: perDay.map((x) => {
          const ov = x.cost !== null && x.costBudget !== null && x.cost > x.costBudget;
          return {
            text: x.uncalc ? NOT_YET : yen(x.cost),
fg: ov ? mock.secondary : mock.textMain,
bg: ov ? mock.errorBg : 'transparent',
mark: ov ? '▲' : '',
markFg: mock.secondary,
            tip: ov && x.cost !== null && x.costBudget !== null ? `${md(x.date)}：人件費予算 ${yen(x.costBudget)} を ${yen(x.cost - x.costBudget)} 超過` : '',
          };
        }),
        total: anyUncalc ? NOT_YET : yen(totalCost),
      },
      {
        label: '交通費',
        labelTip: TRANSPORTATION_TIP,
bg: '#fff',
align: 'right',
        cells: perDay.map((x) => plain(x.uncalc ? NOT_YET : yen(x.trans), mock.textSub)),
        total: anyUncalc ? NOT_YET : yen(sum((x) => x.trans)),
      },
    );
  }
  // 売上は予算と実績をセットで出し分ける（9/16 決定。権限マスタの汎用項目6）
  if (showSales) {
    footRows.push(
      {
      label: '売上予算',
bg: '#fff',
align: 'right',
      cells: perDay.map((x) => plain(yen(x.sales), mock.textSub)),
      total: yen(totalSales),
    },
    {
      label: '売上実績',
bg: '#fff',
align: 'right',
      cells: perDay.map((x) => plain(x.salesAct === null ? '—' : yen(x.salesAct), x.salesAct === null ? mock.gray40 : x.sales !== null && x.salesAct < x.sales ? mock.secondary : mock.success)),
      total: yen(sum((x) => x.salesAct)),
    },
    );
  }
  if (showLaborCost && showSales) {
    footRows.push({
      label: '人件費率',
bg: mock.primaryM95,
align: 'right',
      cells: perDay.map((x) => plain(x.rate === null ? '—' : `${x.rate.toFixed(1)}%`)),
      total: totalRate === null ? '—' : `${totalRate.toFixed(1)}%`,
    });
  }

  const minWidth = NAME_W + colMin * board.days.length + SUM_W;
  // 月をまたぐ期間は日付の上に月の行を足す（9/16 決定）
  const months = monthSpans(board.days.map((d) => d.date));

  return (
    <div css={styles.card}>
      <div css={styles.scroll} ref={scrollRef}>
        <div style={{ minWidth: `${minWidth}px`, ['--sb-col-min' as any]: `${colMin}px` } as React.CSSProperties}>
          {months.length > 0 && (
            <div css={styles.monthRow}>
              <div
                css={styles.headCell}
                style={{
 width: `${NAME_W}px`, flex: 'none', padding: '3px 8px', position: 'sticky', left: 0, zIndex: 4,
}}
              />
              {months.map((m) => (
                <div key={m.label} css={styles.monthCell} style={{ flex: `${m.span} 1 0`, minWidth: `${colMin * m.span}px` }}>{m.label}</div>
              ))}
              <div css={styles.headCell} style={{ width: `${SUM_W}px`, flex: 'none' }} />
            </div>
          )}
          <div css={styles.headRow}>
            <div
              css={styles.headCell}
              style={{
 width: `${NAME_W}px`, flex: 'none', padding: '6px 8px', position: 'sticky', left: 0, zIndex: 4,
}}
            >
              スタッフ / 日付
            </div>
            {board.days.map((d) => (
              <div
                key={d.date}
                css={styles.dateHead}
                style={{ background: d.locked ? mock.gray10 : d.dow === 0 || d.dow === 6 || d.publicHolidayName ? mock.primaryM90 : mock.primaryM95 }}
                title={[d.publicHolidayName, d.isStoreHoliday ? '店休日' : null, d.shopPlan, d.confirmed ? '確定済み' : null, d.achievementConfirmed ? '本部確定済み' : null].filter(Boolean).join(' / ')}
              >
                <div style={{
 fontSize: '11px', fontWeight: 700, color: d.publicHolidayName ? mock.secondary : dowColor(d.dow), textDecoration: d.date === today ? 'underline' : undefined,
}}
                >
                  {md(d.date)}
                </div>
                <div style={{ fontSize: '10px', color: dowColor(d.dow) }}>
                  {DOW_LABELS[d.dow]}
                  {d.confirmed ? ' 確' : ''}
                  {d.isStoreHoliday ? ' 休' : ''}
                  {d.achievementConfirmed ? ' 本' : ''}
                </div>
              </div>
            ))}
            <div
              css={styles.headCell}
              style={{
                width: `${SUM_W}px`, flex: 'none', borderLeft: `1px solid ${mock.primaryM80}`, textAlign: 'center', padding: '6px 0',
              }}
            >
              合計
            </div>
          </div>

          {board.staffs.map((staff) => {
            const rowVio = !!vioStaff[staff.staffCode];
            const tot = byStaff[staff.staffCode];
            let simDays = 0;
            let simMinutes = 0;
            let rowUncalc = false;
            const submitted = staffSubmitted(staff.staffCode);
            const sub = staff.isGuest ? `支援 / ${staff.belongOrgName}所属` : submitted === false ? '未提出' : (staff.executiveName || ''); // 名前の下は役職（9/24）。雇用形態はスタッフ詳細で出す
            const subFg = staff.isGuest ? mock.guestText : submitted === false ? mock.secondary : mock.textSub;
            const cells = board.days.map((day) => {
              const key = `${staff.staffCode}|${day.date}`;
              const cell = getCell(staff.staffCode, day.date);
              const aways = awayShifts(staff.staffCode, day.date);
              const live = cell.shifts.filter((s) => !s.deleted);
              if (live.some((s) => s.attendType === 0)) {
                simDays += 1;
                simMinutes += live.filter((s) => s.attendType === 0).reduce((t, s) => t + workOf(s), 0);
              }
              if (live.some(uncalculated)) {
                rowUncalc = true;
              }
              const locked = day.locked || (staff.isGuest && live.length === 0 && aways.length === 0);
              const wish = hopeOf(staff.staffCode, day.date);
              const msgs = vioCells[key];
              const weekend = day.dow === 0 || day.dow === 6;
              // 公開済みの日でも、何も入っていないセルは通常の見た目のまま（グレー・ロックの点を出さない。9/24 の依頼）
              const emptyConfirmed = day.locked && day.lockReason === 'SHIFT_CONFIRMED' && !staff.isGuest && live.length === 0 && aways.length === 0;
              const bg = locked && !emptyConfirmed ? mock.gray10 : msgs ? mock.errorBg : (wish && !wish.isOff ? mock.primaryM95 : weekend ? '#FAFAFA' : '#FFFFFF');
              const tip = `${staff.isGuest ? `${staff.belongOrgName}所属の支援スタッフ（所属店で登録・編集）\n` : ''}${locked ? `${day.lockReason === 'SHIFT_CONFIRMED' ? '公開済み（「ロック解除」で編集できます）' : day.lockReason === 'ACHIEVEMENT_CONFIRMED' ? '本部確定済み' : 'この店舗からは編集できません'}\n` : bulkMode ? `セルの空いているところをクリックで「${bulkHint}」を割当 / カードをクリックで詳細（「割当を外す」を選んでいるときはそのカードを外す）\n` : live.length ? 'カードをクリックで個別に編集 / ドラッグで移動 / shift+ドラッグでコピー\n' : ''}${msgs ? `【違反】${msgs.join(' / ')}\n` : ''}${staff.staffName} ${md(day.date)}（${DOW_LABELS[day.dow]}）\n希望：${wish ? (wish.isOff ? '休み' : wish.label) : '未提出'}\n割当：${live.length ? live.map((s) => `${chipLabel(s, staff.distinctionHoliday)}${s.attendType === 0 && s.start ? ` ${s.start}–${s.end}${s.isEndNextDay ? '翌' : ''}` : ''}`).join(' / ') : 'なし'}`;
              return (
                <div
                  key={day.date}
                  data-cell={key}
                  css={styles.cell}
                  data-flash={flashKey === key}
                  data-drop={dropKey === key}
                  title={tip}
                  style={{ background: bg, cursor: locked ? (staff.isGuest ? 'not-allowed' : 'default') : 'pointer' }}
                  onClick={() => {
                    if (longPressed.current) {
                      longPressed.current = false;
                      return;
                    }
                    onCellClick(staff, day, null);
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    if (!locked && editable) {
                      onCellClear(staff, day);
                    }
                  }}
                  onPointerDown={pointerDown(staff, day)}
                  onPointerMove={pointerMove}
                  onPointerUp={pointerUp}
                  onPointerCancel={pointerUp}
                >
                  {wish && <div css={styles.wish} style={{ color: wish.isOff ? mock.gray40 : mock.primary }} title={`希望 ${wish.label}`}>{`希 ${wish.isOff ? '休み' : wish.label}`}</div>}
                  {(cell.shifts.length > 0 || aways.length > 0) && (
                    <div css={styles.chips}>
                      {cell.shifts.map((s, i) => {
                        const c = s.attendType !== 0 ? { bg: mock.gray10, fg: mock.gray60 } : colorOf(s.shiftPatternId);
                        const fs = cell.shifts.length + aways.length > 2 ? '8px' : cell.shifts.length + aways.length === 2 ? '9px' : '11px';
                        return (
                          <div
                            key={i}
                            css={styles.chip}
                            style={{
                              background: staff.isGuest ? `${mock.hatch}, ${c.bg}` : c.bg,
                              color: c.fg,
                              border: staff.isGuest ? '1px solid rgba(0,0,0,.28)' : (!s.shiftId || cell.changed ? `1px dashed ${mock.primary}` : '1px solid transparent'),
                              fontSize: fs,
                              cursor: locked || !editable ? 'default' : 'grab',
                              textDecoration: s.deleted ? 'line-through' : undefined,
                              opacity: s.deleted ? 0.5 : 1,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (longPressed.current) {
                                longPressed.current = false;
                                return;
                              }
                              onCellClick(staff, day, i);
                            }}
                            onPointerDown={(e) => {
                              if (locked || !editable || s.deleted) {
                                return;
                              }
                              if (e.pointerType === 'mouse') {
                                onChipDragStart(staff, day, i, e);
                              }
                            }}
                          >
                            {cell.shifts.length + aways.length === 1 && s.attendType === 0 && s.start ? (
                              <React.Fragment>
                                <div style={{ fontSize: '9px', fontWeight: 500, lineHeight: 1.2 }}>{s.shiftPatternShortName || 'フリー'}</div>
                                <div style={{ fontSize: '11px', lineHeight: 1.2 }}>{s.start}</div>
                                <div style={{ fontSize: '11px', lineHeight: 1.2 }}>{`${s.end || ''}${s.isEndNextDay ? '翌' : ''}`}</div>
                              </React.Fragment>
                            ) : chipLabel(s, staff.distinctionHoliday)}
                          </div>
                        );
                      })}
                      {aways.map((a) => (
                        <div
                          key={a.shiftId}
                          css={styles.chip}
                          style={{
 background: '#fff', color: mock.gray60, border: `1px dashed ${mock.gray40}`, fontSize: '9px',
}}
                          title={`他店へ支援: ${a.awayOrgName || ''}`}
                        >
                          {`→${(a.awayOrgName || '支援').slice(0, 4)} ${a.attendType !== 0 ? '公休' : a.shiftPatternShortName || `${a.start}–${a.end}`}`}
                        </div>
                      ))}
                    </div>
                  )}
                  {msgs && (
                  <div style={{
 position: 'absolute', inset: 0, border: `2px solid ${mock.error}`, pointerEvents: 'none',
}}
                  />
)}
                  {!locked && editable && live.length === 0 && aways.length === 0 && (
                    <div css={styles.addHint} data-addhint="1"><div css={styles.addHintInner}>{bulkMode ? `+ ${bulkHint}` : '+ 追加'}</div></div>
                  )}
                  {live.some((s) => !!s.memo) && <div css={styles.memo} title="勤務メモあり" />}
                  {failedOf && failedOf(staff.staffCode, day.date) ? (
                    <div css={styles.failMark} title={`保存できませんでした: ${failedOf(staff.staffCode, day.date)}\n保存前の内容に戻しています。もう一度編集してください`}>!</div>
                  ) : cell.changed && <div css={styles.changedDot} title="保存中" />}
                  {locked && !emptyConfirmed && live.length === 0 && aways.length === 0 && <div css={styles.lockMark} title="ロック中">●</div>}
                </div>
              );
            });
            // 時間・日数はいつもセルから数える。未計算のシフトがあれば時間は目安（約）、人件費・交通費は「未計算」（9/25）
            const rowMinutes = simMinutes;
            const rowDays = simDays;
            const totalBg = tot && tot.weeklyOver ? mock.errorBg : mock.primaryM95;
            return (
              <div key={staff.staffCode} css={styles.row}>
                <div css={styles.nameCell} style={{ background: rowVio ? mock.errorBg : staff.isGuest ? mock.guestBg : '#fff' }} onClick={() => onStaffTap(staff)} title="スタッフ詳細">
                  <StaffAvatar staff={staff} src={photoOf(staff)} hasViolation={!!rowVio} />
                  <div style={{ minWidth: 0 }}>
                    <div css={styles.name}>{staff.staffName}</div>
                    <div style={{
 fontSize: '10px', color: subFg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
}}
                    >
                      {sub}
                    </div>
                  </div>
                </div>
                {cells}
                {/*
                  合計は 1 列（9/24 案D）。9/25 に項目名を付けて組み直した
                    1 段目: 時間（大きく）と日数
                    2・3 段目: 人件費・交通費。左に項目名、金額は右にそろえる
                */}
                <div
                  css={styles.totalCell}
                  style={{
                    width: `${SUM_W}px`, alignItems: 'stretch', background: totalBg, borderLeft: `1px solid ${mock.primaryM80}`, fontVariantNumeric: 'tabular-nums', lineHeight: 1.3, gap: '1px', padding: '0 6px',
                  }}
                  title={showLaborCost && tot ? `人件費 ${yen(tot.laborCost)}（交通費 ${yen(tot.transportation)} を含む）${staff.hourlySalary ? ` / 時給¥${staff.hourlySalary.toLocaleString('ja-JP')}` : ''}${staff.dailyTransportation ? ` / 交通費 ¥${staff.dailyTransportation}/日` : ''}` : undefined}
                >
                  <div style={{
 display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: '4px',
}}
                  >
                    <span style={{ fontSize: '13px', fontWeight: 700, color: tot && tot.weeklyOver ? mock.secondary : mock.textMain }} title={rowUncalc ? '確定して公開すると正しく計算されます（今は開始・終了・休憩からの目安）' : undefined}>{`${hours(rowMinutes)}`}</span>
                    <span style={{ fontSize: '10px', color: mock.textSub }}>{`${rowDays}日`}</span>
                  </div>
                  {showLaborCost && (
                    <div style={{
 display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '4px',
}}
                    >
                      <span style={{ fontSize: '9px', color: mock.textSub }} title={LABOR_COST_TIP}>人件費</span>
                      <span style={{ fontSize: rowUncalc ? '10px' : '11px', fontWeight: 700, color: rowUncalc ? mock.textSub : undefined }}>{rowUncalc ? NOT_YET : (tot ? yen(tot.laborCost) : '—')}</span>
                    </div>
                  )}
                  {showLaborCost && (
                    <div style={{
 display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '4px',
}}
                    >
                      <span style={{ fontSize: '9px', color: mock.textSub }} title={TRANSPORTATION_TIP}>交通費</span>
                      <span style={{ fontSize: rowUncalc ? '10px' : '11px', color: rowUncalc ? mock.textSub : undefined }}>{rowUncalc ? NOT_YET : (tot ? yen(tot.transportation) : '—')}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {footRows.map((fr) => (
            <div key={fr.label} css={styles.footRow} style={{ background: fr.bg }}>
              <div css={styles.footLabel} title={fr.labelTip}>{fr.label}</div>
              {fr.cells.map((t, i) => (
                <div key={i} css={styles.footCell} style={{ textAlign: fr.align, color: t.fg, background: t.bg }} title={t.tip}>
                  {t.mark && <span css={styles.mark} style={{ color: t.markFg }}>{t.mark}</span>}
                  {t.text}
                </div>
              ))}
              <div css={styles.footTotal} style={{ width: `${SUM_W}px`, textAlign: 'right', borderLeft: `1px solid ${mock.primaryM80}` }}>{fr.total}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BoardGrid;
